const CART_CONTEXT_KEY = "cartContext";
const TABLE_CART_MAP_KEY = "tableCartMap";

const getStoredTableCartMap = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = sessionStorage.getItem(TABLE_CART_MAP_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((entry) => entry && typeof entry === "object");
    }
  } catch {
    // ignore malformed storage and fall back to empty array
  }

  return [];
};

export const getCartContext = (tableId = null) => {
  if (typeof window === "undefined") {
    return { cartId: null, tableId: null };
  }

  if (tableId) {
    const tableEntry = getStoredTableCartMap().find(
      (entry) => String(entry?.tableId) === String(tableId)
    );

    if (tableEntry?.cartId) {
      return {
        cartId: String(tableEntry.cartId),
        tableId: String(tableId),
      };
    }

    // When a table is explicitly requested, do not fall back to global cart ids.
    // This prevents reusing another table's cart on first add.
    return {
      cartId: null,
      tableId: String(tableId),
    };
  }

  try {
    const raw = sessionStorage.getItem(CART_CONTEXT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return {
          cartId: parsed?.cartId ?? parsed?.orderHeadId ?? null,
          tableId: parsed?.tableId ?? null,
        };
      }
    }
  } catch {
    // fall back to the legacy cartId storage below
  }

  const legacyCartId = sessionStorage.getItem("cartId");
  const cartId =
    legacyCartId && legacyCartId !== "undefined" && legacyCartId !== "null"
      ? String(legacyCartId)
      : null;

  return { cartId, tableId: null };
};

export const getCartId = () => getCartContext().cartId;

export const getCartTableId = () => getCartContext().tableId;

export const getGlobalCartId = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const legacyCartId = sessionStorage.getItem("cartId");
  return legacyCartId && legacyCartId !== "undefined" && legacyCartId !== "null"
    ? String(legacyCartId)
    : null;
};

export const setGlobalCartId = (cartId) => {
  if (typeof window !== "undefined") {
    if (cartId) {
      sessionStorage.setItem("cartId", String(cartId));
    } else {
      sessionStorage.removeItem("cartId");
    }
  }

  return cartId ? String(cartId) : null;
};

export const clearGlobalCartId = () => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem("cartId");
  }

  return null;
};

export const getTableCartId = (tableId) => {
  if (!tableId) {
    return null;
  }

  const tableEntry = getStoredTableCartMap().find(
    (entry) => String(entry?.tableId) === String(tableId)
  );

  return tableEntry?.cartId ? String(tableEntry.cartId) : null;
};

export const setTableCartId = (tableId, cartId) => {
  if (typeof window === "undefined" || !tableId) {
    return null;
  }

  const normalizedTableId = String(tableId);
  const tableCartMap = getStoredTableCartMap();
  const existingIndex = tableCartMap.findIndex(
    (entry) => String(entry?.tableId) === normalizedTableId
  );

  if (cartId) {
    const nextEntry = { tableId: normalizedTableId, cartId: String(cartId) };
    if (existingIndex >= 0) {
      tableCartMap[existingIndex] = nextEntry;
    } else {
      tableCartMap.push(nextEntry);
    }
  } else if (existingIndex >= 0) {
    tableCartMap.splice(existingIndex, 1);
  }

  sessionStorage.setItem(TABLE_CART_MAP_KEY, JSON.stringify(tableCartMap));
  return cartId ? String(cartId) : null;
};

export const clearTableCartId = (tableId) => {
  if (typeof window === "undefined" || !tableId) {
    return null;
  }

  const normalizedTableId = String(tableId);
  const tableCartMap = getStoredTableCartMap().filter(
    (entry) => String(entry?.tableId) !== normalizedTableId
  );
  sessionStorage.setItem(TABLE_CART_MAP_KEY, JSON.stringify(tableCartMap));
  return null;
};

export const setCartContext = ({ cartId, tableId = null }) => {
  const context = {
    cartId: cartId ?? null,
    tableId: tableId ?? null,
  };

  if (typeof window !== "undefined") {
    const tableCartMap = getStoredTableCartMap();

    if (tableId) {
      const normalizedTableId = String(tableId);
      const existingIndex = tableCartMap.findIndex(
        (entry) => String(entry?.tableId) === normalizedTableId
      );

      if (cartId) {
        if (existingIndex >= 0) {
          tableCartMap[existingIndex] = { tableId: normalizedTableId, cartId: String(cartId) };
        } else {
          tableCartMap.push({ tableId: normalizedTableId, cartId: String(cartId) });
        }
      } else if (existingIndex >= 0) {
        tableCartMap.splice(existingIndex, 1);
      }

      sessionStorage.setItem(TABLE_CART_MAP_KEY, JSON.stringify(tableCartMap));
    }

    sessionStorage.setItem(CART_CONTEXT_KEY, JSON.stringify(context));

    if (context.cartId) {
      sessionStorage.setItem("cartId", String(context.cartId));
    } else {
      sessionStorage.removeItem("cartId");
    }
  }

  return context;
};

export const clearCartContext = () => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(CART_CONTEXT_KEY);
    sessionStorage.removeItem(TABLE_CART_MAP_KEY);
    sessionStorage.removeItem("cartId");
  }

  return { cartId: null, tableId: null };
};

// The table list API reports the order head that is currently open on each
// table (activeOrderHeadId), plus the open phone and online orders, which
// have no table and are tracked under "phone-<orderHeadId>" /
// "online-<orderHeadId>" ids instead. Mirroring those into the table -> cart
// map keeps the order details panel working for orders started on another
// terminal, or before this browser session existed.
//
// Accepts either the raw API response ({ tableTypes, phoneOrders,
// onlineOrders }) or the legacy table types array.
export const syncTableCartIdsFromTables = (payload) => {
  if (typeof window === "undefined") {
    return [];
  }

  const tableTypes = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.tableTypes)
      ? payload.tableTypes
      : [];
  const phoneOrders = Array.isArray(payload?.phoneOrders)
    ? payload.phoneOrders
    : [];
  const onlineOrders = Array.isArray(payload?.onlineOrders)
    ? payload.onlineOrders
    : [];

  const changed = [];

  const sync = (tableId, orderHeadId) => {
    if (!tableId) return;

    const activeOrderHeadId =
      orderHeadId != null && orderHeadId !== "" ? String(orderHeadId) : null;

    // Only the server knows whether a table still has an open order, but a
    // cart created locally may not be reflected in the response yet, so a
    // missing id never clears a locally tracked one.
    if (!activeOrderHeadId) return;

    if (getTableCartId(tableId) === activeOrderHeadId) return;

    setTableCartId(tableId, activeOrderHeadId);
    changed.push({ tableId: String(tableId), cartId: activeOrderHeadId });
  };

  tableTypes.forEach((type) => {
    (type?.tables || []).forEach((table) => {
      sync(table?.tableId, table?.activeOrderHeadId);
    });
  });

  phoneOrders.forEach((order) => {
    if (order?.orderHeadId == null) return;
    sync(`phone-${order.orderHeadId}`, order.orderHeadId);
  });

  onlineOrders.forEach((order) => {
    if (order?.orderHeadId == null) return;
    sync(`online-${order.orderHeadId}`, order.orderHeadId);
  });

  changed.forEach(({ tableId, cartId }) => {
    window.dispatchEvent(
      new CustomEvent("pos:cart-id-changed", { detail: { cartId, tableId } })
    );
  });

  return changed;
};
